import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const initial = params.get("token") || "";
  const [token, setToken] = useState(initial);
  const [pw, setPw] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset! Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6" data-testid="reset-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-md bg-[#1C3F35] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5}/>
          </div>
          <span className="font-heading text-lg">Beacon of Grants</span>
        </Link>

        <h1 className="font-heading font-light text-4xl tracking-tighter text-[#1A1A1A]">
          Set a new password
        </h1>

        <form onSubmit={submit} className="mt-10 space-y-5">
          <div>
            <Label htmlFor="token">Reset token</Label>
            <Input
              id="token" required value={token}
              onChange={(e)=>setToken(e.target.value)}
              data-testid="reset-token"
              className="mt-2 bg-white border-[#E6E4DF] font-mono text-xs"
            />
          </div>
          <div>
            <Label htmlFor="pw">New password</Label>
            <div className="relative mt-2">
              <Input
                id="pw" type={showPassword ? "text" : "password"} required minLength={6}
                value={pw} onChange={(e)=>setPw(e.target.value)}
                data-testid="reset-password"
                className="bg-white border-[#E6E4DF] pr-10"
              />
              <button
                type="button"
                onClick={()=>setShowPassword((s)=>!s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="reset-password-toggle"
                className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5E5E5E] hover:text-[#1C3F35] transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
          </div>
          <Button
            type="submit" disabled={loading}
            data-testid="reset-submit"
            className="w-full bg-[#1C3F35] text-white hover:bg-[#1A362D] rounded-md py-6"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Reset password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
